import { useState } from "react";
import { ShieldCheck, Download, Trash2, Loader2, AlertTriangle } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

export function GdprPanel() {
  const { user, logout } = useAuth();
  const [exporting, setExporting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmText, setConfirmText] = useState("");

  const exportData = async () => {
    setExporting(true);
    try {
      const r = await api.get("/gdpr/export");
      const blob = new Blob([JSON.stringify(r.data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `oxo-date-${(user?.email || "cont").split("@")[0]}.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Datele tale au fost exportate (JSON).");
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally { setExporting(false); }
  };

  const deleteAccount = async () => {
    setDeleting(true);
    try {
      await api.post("/gdpr/delete-account", {});
      toast.success("Contul a fost șters (anonimizat) conform GDPR.");
      setConfirmOpen(false);
      setTimeout(() => { logout(); window.location.href = "/"; }, 1200);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
      setDeleting(false);
    }
  };

  return (
    <section className="border border-slate-200 p-4 mt-8" data-testid="gdpr-panel">
      <h2 className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500 mb-1 flex items-center gap-2">
        <ShieldCheck className="w-4 h-4" /> Confidențialitate & date (GDPR)
      </h2>
      <p className="text-sm text-slate-500 mb-4 leading-relaxed">
        Conform GDPR (UE 2016/679) îți poți exporta datele sau șterge contul oricând. Operator: OXO SRL (RO6926354), Miercurea Ciuc · gdpr@oxo.ro.
      </p>
      <div className="flex flex-col sm:flex-row gap-3">
        <button onClick={exportData} disabled={exporting}
          className="inline-flex items-center justify-center gap-2 border border-slate-300 hover:border-slate-900 text-slate-800 px-4 py-2.5 text-sm font-bold transition-colors disabled:opacity-50" data-testid="gdpr-export-btn">
          {exporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />} Exportă datele mele (JSON)
        </button>
        <button onClick={() => { setConfirmText(""); setConfirmOpen(true); }}
          className="inline-flex items-center justify-center gap-2 border border-red-300 text-red-600 hover:bg-red-50 px-4 py-2.5 text-sm font-bold transition-colors" data-testid="gdpr-delete-btn">
          <Trash2 className="w-4 h-4" /> Șterge contul
        </button>
      </div>

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent className="max-w-md rounded-none" data-testid="gdpr-delete-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base font-black text-red-600"><AlertTriangle className="w-5 h-5" /> Ștergerea contului</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-600 leading-relaxed">
            Contul tău va fi <b>anonimizat definitiv</b>: datele personale (nume, email, telefon) sunt eliminate. Anunțurile rămân publicate sub „Utilizator șters" pentru integritatea pieței. Acțiunea este ireversibilă.
          </p>
          <p className="text-sm text-slate-600 mt-1">Scrie <b>ȘTERGE</b> pentru a confirma:</p>
          <input value={confirmText} onChange={(e) => setConfirmText(e.target.value)}
            className="w-full px-3 py-2.5 border border-slate-300 text-sm focus:outline-none focus:border-red-500" data-testid="gdpr-confirm-input" placeholder="ȘTERGE" />
          <div className="flex gap-2 justify-end mt-2">
            <button onClick={() => setConfirmOpen(false)} className="px-4 py-2.5 text-sm font-semibold text-slate-600 hover:text-slate-900" data-testid="gdpr-cancel">Anulează</button>
            <button onClick={deleteAccount} disabled={confirmText.trim() !== "ȘTERGE" || deleting}
              className="inline-flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white px-4 py-2.5 text-sm font-bold transition-colors disabled:opacity-40 disabled:cursor-not-allowed" data-testid="gdpr-confirm-delete">
              {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />} Șterge definitiv
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </section>
  );
}
